import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import cn from 'classnames';
import { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Button, Icon, SlotSelectionSnapshot } from '@/shared/ui';

import { StoredSection, SectionOptionSchema } from '@/entities/templates';

import {
  AutoFocusTarget,
  DropSide,
  EMPTY_SECTIONS_DROPZONE_ID,
  PALETTE_DRAG_TYPES,
  PaletteDragType,
  templateEditorMessages,
} from '../../config';
import { buildOptionDropId, getAiOptionIndex, getSectionIds } from '../../lib';
import { EditorButton } from '../EditorButton';

import { AiOptionRow } from './AiOptionRow';
import { DropZonePlaceholder } from './DropZonePlaceholder';
import { OptionRow } from './OptionRow';
import { SectionBlock } from './SectionBlock';
import styles from './TemplateSectionsEditor.module.scss';

// The Lexical editors report their initial value on mount (PlainLexicalEditor
// with line breaks flattened to spaces); treating that echo as an edit would
// dirty the form.
const isEditorEcho = (stored: string | undefined, text: string): boolean =>
  stored !== undefined &&
  (stored === text || stored.replace(/\n/g, ' ') === text);

type TemplateSectionsEditorProps = {
  sections: StoredSection[];
  readOnly?: boolean;
  // Options are a Protocol-only concept: the submit path drops them for other
  // document types, so the controls that create them must not be offered.
  allowOptions?: boolean;
  activeSectionIndex?: number | null;
  autoFocusTarget: AutoFocusTarget | null;
  activeDragType?: PaletteDragType | 'reorder' | null;
  dropIndicatorId?: string | null;
  dropIndicatorSide?: DropSide;
  errorSectionIndex?: number | null;
  editorInsertSlotRef?: React.MutableRefObject<
    ((slotName: string) => void) | null
  >;
  editorSelectionTextRef?: React.MutableRefObject<SlotSelectionSnapshot | null>;
  onSectionsChange: (sections: StoredSection[]) => void;
  onRequiredChange: (sectionIndex: number, required: boolean) => void;
  onOpenSectionAiInstructions: (sectionIndex: number) => void;
  onOpenOptionAiInstructions: (
    sectionIndex: number,
    optionIndex: number,
  ) => void;
  onSlotClick?: (
    sectionIndex: number,
    optionIndex: number,
    slotName: string,
  ) => void;
  onSlotCreate?: (
    sectionIndex: number,
    optionIndex: number,
    slotName: string,
    firstValue: string,
  ) => void;
  onAddAiOption?: (sectionIndex: number) => void;
  onClickAddSection?: () => void;
  onAddSectionAt?: (insertIndex: number) => void;
  onAddOptionAt?: (sectionIndex: number, insertIndex: number) => void;
  onPromoteOptionToSection?: (
    sectionIndex: number,
    optionIndex: number,
  ) => void;
  onDemoteSectionToOption?: (sectionIndex: number) => void;
  onEditorFocus?: (sectionIndex: number, optionIndex: number) => void;
  onEditorBlur?: () => void;
  onActiveSectionChange?: (sectionIndex: number) => void;
  onSectionFocus?: (sectionIndex: number) => void;
};

export const TemplateSectionsEditor: FC<TemplateSectionsEditorProps> = (
  props,
) => {
  const {
    sections,
    readOnly,
    allowOptions = true,
    activeSectionIndex,
    autoFocusTarget,
    activeDragType,
    dropIndicatorId,
    dropIndicatorSide,
    errorSectionIndex,
    editorInsertSlotRef,
    editorSelectionTextRef,
    onSectionsChange,
    onRequiredChange,
    onOpenSectionAiInstructions,
    onOpenOptionAiInstructions,
    onSlotClick,
    onSlotCreate,
    onAddAiOption,
    onClickAddSection,
    onAddSectionAt,
    onAddOptionAt,
    onPromoteOptionToSection,
    onDemoteSectionToOption,
    onEditorFocus: onEditorFocusProp,
    onEditorBlur: onEditorBlurProp,
    onActiveSectionChange,
    onSectionFocus,
  } = props;

  const { formatMessage } = useIntl();

  const sectionIds = getSectionIds(sections);

  const getDropIndicatorSide = (
    dragType: PaletteDragType,
    targetId: string,
  ): DropSide | null =>
    activeDragType === dragType && dropIndicatorId === targetId
      ? dropIndicatorSide ?? 'after'
      : null;

  const dropIndicatorLine = (
    <div
      data-component="template-drop-indicator"
      className={styles.dropIndicatorLine}
    />
  );

  const handleTitleChange = useCallback(
    (sectionIndex: number, title: string) => {
      if (isEditorEcho(sections[sectionIndex]?.title, title)) {
        return;
      }

      const updated = sections.map((section, index) =>
        index === sectionIndex ? { ...section, title } : section,
      );
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleDeleteSection = useCallback(
    (sectionIndex: number) => {
      const updated = sections.filter((_, index) => index !== sectionIndex);
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleOptionTextChange = useCallback(
    (sectionIndex: number, optionIndex: number, text: string) => {
      if (
        isEditorEcho(sections[sectionIndex]?.options?.[optionIndex]?.text, text)
      ) {
        return;
      }

      const updated = sections.map((section, sIdx) => {
        if (sIdx !== sectionIndex) {
          return section;
        }

        const updatedOptions = (section.options ?? []).map((option, oIdx) =>
          oIdx === optionIndex ? { ...option, text } : option,
        );

        return { ...section, options: updatedOptions };
      });
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleDeleteOption = useCallback(
    (sectionIndex: number, optionIndex: number) => {
      const updated = sections.map((section, sIdx) => {
        if (sIdx !== sectionIndex) {
          return section;
        }

        const updatedOptions = (section.options ?? []).filter(
          (_, oIdx) => oIdx !== optionIndex,
        );

        return { ...section, options: updatedOptions };
      });
      onSectionsChange(updated);
    },
    [sections, onSectionsChange],
  );

  const handleEditorFocus = useCallback(
    (sectionIndex: number, optionIndex: number) => {
      onActiveSectionChange?.(sectionIndex);
      onEditorFocusProp?.(sectionIndex, optionIndex);
    },
    [onActiveSectionChange, onEditorFocusProp],
  );

  const handleEditorBlur = useCallback(() => {
    onEditorBlurProp?.();
  }, [onEditorBlurProp]);

  const handleSectionFocus = useCallback(
    (sectionIndex: number) => {
      onSectionFocus?.(sectionIndex);
      onActiveSectionChange?.(sectionIndex);
    },
    [onActiveSectionChange, onSectionFocus],
  );

  const handleSectionTabPress = useCallback(
    (sectionIndex: number, isShiftPressed: boolean) => {
      if (!isShiftPressed && allowOptions) {
        onDemoteSectionToOption?.(sectionIndex);
      }
    },
    [allowOptions, onDemoteSectionToOption],
  );

  const handleOptionTabPress = useCallback(
    (sectionIndex: number, optionIndex: number, isShiftPressed: boolean) => {
      if (isShiftPressed) {
        onPromoteOptionToSection?.(sectionIndex, optionIndex);
      }
    },
    [onPromoteOptionToSection],
  );

  if (sections.length === 0) {
    return (
      <div className={styles.container}>
        <DropZonePlaceholder
          id={EMPTY_SECTIONS_DROPZONE_ID}
          className={cn('p3', styles.emptyPlaceholder)}
          activeClassName={styles.emptyPlaceholderActive}
          isActive={activeDragType === PALETTE_DRAG_TYPES.SECTION}
        >
          <div className={styles.emptyState}>
            <Icon name="file" size={40} className={styles.emptyIcon} />
            <p className={cn('p3', styles.emptyText)}>
              {formatMessage(templateEditorMessages.emptyEditorTitle)}
            </p>
            {!readOnly && onClickAddSection && (
              <Button
                variant="secondary"
                size="medium"
                icon="plus"
                onClick={onClickAddSection}
              >
                {formatMessage(templateEditorMessages.addSectionButton)}
              </Button>
            )}
          </div>
        </DropZonePlaceholder>
      </div>
    );
  }

  return (
    <div className={styles.container} data-component="template-sections-editor">
      <div className={styles.sectionsList}>
        <SortableContext
          items={sectionIds}
          strategy={verticalListSortingStrategy}
        >
          {sections.map((section, sectionIndex) => {
            const sectionId = sectionIds[sectionIndex];
            // Non-Protocol templates never show options, even if a stale
            // draft still carries them: rendering would invite edits that
            // the submit path drops.
            const options: SectionOptionSchema[] = allowOptions
              ? section.options ?? []
              : [];
            const aiOptionIndex = allowOptions ? getAiOptionIndex(section) : -1;
            const strictOptionEntries = options
              .map((option, optionIndex) => ({
                option,
                optionIndex,
                optionId: buildOptionDropId({ sectionId, optionIndex }),
              }))
              .filter(({ optionIndex }) => optionIndex !== aiOptionIndex);
            const optionIds = strictOptionEntries.map(
              ({ optionId }) => optionId,
            );

            const isSectionAutoFocused =
              autoFocusTarget?.sectionIndex === sectionIndex &&
              autoFocusTarget?.optionIndex === undefined;
            const sectionDropSide = getDropIndicatorSide(
              PALETTE_DRAG_TYPES.SECTION,
              sectionId,
            );

            return (
              <div
                key={sectionId}
                className={styles.sectionWrapper}
                data-section-anchor={sectionIndex}
              >
                {sectionDropSide === 'before' && dropIndicatorLine}
                <SectionBlock
                  id={sectionId}
                  sectionIndex={sectionIndex}
                  section={section}
                  isActive={activeSectionIndex === sectionIndex}
                  hasError={errorSectionIndex === sectionIndex}
                  readOnly={readOnly}
                  autoFocus={isSectionAutoFocused}
                  onTitleChange={handleTitleChange}
                  onDeleteSection={handleDeleteSection}
                  onRequiredChange={onRequiredChange}
                  onOpenAiInstructions={onOpenSectionAiInstructions}
                  onFocus={handleSectionFocus}
                  onEnterPress={(index) =>
                    allowOptions
                      ? onAddOptionAt?.(index, options.length)
                      : onAddSectionAt?.(index + 1)
                  }
                  onTabPress={handleSectionTabPress}
                >
                  <SortableContext
                    items={optionIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {strictOptionEntries.map(
                      ({ option, optionIndex, optionId }, position) => {
                        const isOptionAutoFocused =
                          autoFocusTarget?.sectionIndex === sectionIndex &&
                          autoFocusTarget?.optionIndex === optionIndex;
                        const optionDropSide = getDropIndicatorSide(
                          PALETTE_DRAG_TYPES.OPTION,
                          optionId,
                        );

                        return (
                          <div key={optionId} className={styles.optionWrapper}>
                            {optionDropSide === 'before' && dropIndicatorLine}
                            <OptionRow
                              id={optionId}
                              optionIndex={optionIndex}
                              displayNumber={`${sectionIndex + 1}.${position + 1}`}
                              sectionIndex={sectionIndex}
                              option={option}
                              isFieldDropTarget={
                                activeDragType === PALETTE_DRAG_TYPES.FIELD &&
                                dropIndicatorId === optionId
                              }
                              readOnly={readOnly}
                              autoFocus={isOptionAutoFocused}
                              insertSlotRef={editorInsertSlotRef}
                              selectionTextRef={editorSelectionTextRef}
                              onTextChange={handleOptionTextChange}
                              onDelete={handleDeleteOption}
                              onOpenAiInstructions={onOpenOptionAiInstructions}
                              onSlotClick={onSlotClick}
                              onSlotCreate={onSlotCreate}
                              onEditorFocus={handleEditorFocus}
                              onEditorBlur={handleEditorBlur}
                              onEnterPress={(
                                targetSectionIndex,
                                targetOptionIndex,
                              ) =>
                                onAddOptionAt?.(
                                  targetSectionIndex,
                                  targetOptionIndex + 1,
                                )
                              }
                              onTabPress={handleOptionTabPress}
                            />
                            {optionDropSide === 'after' && dropIndicatorLine}
                          </div>
                        );
                      },
                    )}
                  </SortableContext>

                  {aiOptionIndex >= 0 && (
                    <AiOptionRow
                      displayNumber={`${sectionIndex + 1}.${strictOptionEntries.length + 1}`}
                      readOnly={readOnly}
                      onDelete={() =>
                        handleDeleteOption(sectionIndex, aiOptionIndex)
                      }
                    />
                  )}

                  {!readOnly &&
                    allowOptions &&
                    activeSectionIndex === sectionIndex && (
                      <div className={styles.sectionActions}>
                        <EditorButton
                          variant="outlineViolet"
                          icon="plus"
                          onClick={() =>
                            onAddOptionAt?.(sectionIndex, options.length)
                          }
                        >
                          {formatMessage(
                            templateEditorMessages.addStrictOption,
                          )}
                        </EditorButton>
                        {aiOptionIndex < 0 && (
                          <EditorButton
                            variant="outlineViolet"
                            icon="plus"
                            onClick={() => onAddAiOption?.(sectionIndex)}
                          >
                            {formatMessage(templateEditorMessages.addAiOption)}
                          </EditorButton>
                        )}
                      </div>
                    )}
                </SectionBlock>
                {sectionDropSide === 'after' && dropIndicatorLine}
              </div>
            );
          })}
        </SortableContext>
      </div>
    </div>
  );
};

export type { TemplateSectionsEditorProps };
